// Social sign-in for the member login (Accedi) — mirrors the legacy site exactly: get a provider
// token client-side, then POST it to /security/login as `google_token` / `fb_token`. The backend
// (LoginAuthenticator + SocialLoginService) verifies the token and creates/links the member.
// Uses the SAME public client IDs as the legacy site, supplied via Vite env.

/* eslint-disable @typescript-eslint/no-explicit-any */
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID as string | undefined

export const googleConfigured = Boolean(GOOGLE_CLIENT_ID)
export const facebookConfigured = Boolean(FACEBOOK_APP_ID)

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve()
    const s = document.createElement('script')
    s.src = src
    s.id = id
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`load failed: ${src}`))
    document.head.appendChild(s)
  })
}

/* ── Google Identity Services → ID token (credential), sent as google_token ── */
export async function googleSignIn(): Promise<string> {
  if (!GOOGLE_CLIENT_ID) throw new Error('Google non configurato')
  await loadScript('https://accounts.google.com/gsi/client', 'gsi-client')
  const g = (window as any).google
  if (!g?.accounts?.id) throw new Error('Google SDK non disponibile')
  return new Promise<string>((resolve, reject) => {
    let settled = false
    const settle = (fn: () => void) => {
      if (settled) return
      settled = true
      fn()
    }
    g.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      // Chrome enforces FedCM and blocks the 3rd-party cookies classic One Tap relies on, so without
      // this the prompt is silently suppressed in Chrome (Firefox still works). FedCM fixes Chrome.
      use_fedcm_for_prompt: true,
      callback: (resp: { credential?: string }) =>
        settle(() => (resp?.credential ? resolve(resp.credential) : reject(new Error('Google: token mancante')))),
    })
    g.accounts.id.prompt((n: any) => {
      // These moment methods are deprecated under FedCM (may be no-ops or throw) → guard, and only
      // treat a genuine "couldn't display" as an error (not a user dismissal).
      try {
        if (n?.isNotDisplayed?.() || n?.isSkippedMoment?.()) {
          settle(() => reject(new Error('Accesso Google non disponibile. Riprova o usa email e password.')))
        }
      } catch {
        /* FedCM deprecates the moment API */
      }
    })
    // safety net: if the FedCM dialog is closed with no signal, don't leave the button spinning forever
    setTimeout(() => settle(() => reject(new Error('Accesso Google annullato.'))), 60000)
  })
}

/* ── Facebook SDK → access token, sent as fb_token ── */
let fbReady = false
function fbInit(): Promise<void> {
  if (!FACEBOOK_APP_ID) return Promise.reject(new Error('Facebook non configurato'))
  if (fbReady) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    ;(window as any).fbAsyncInit = () => {
      ;(window as any).FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: false, version: 'v19.0' })
      fbReady = true
      resolve()
    }
    loadScript('https://connect.facebook.net/en_US/sdk.js', 'fb-sdk').catch(reject)
  })
}

export async function facebookSignIn(): Promise<string> {
  await fbInit()
  const FB = (window as any).FB
  return new Promise<string>((resolve, reject) => {
    FB.login(
      (resp: any) => {
        const tok = resp?.authResponse?.accessToken
        if (resp?.status === 'connected' && tok) resolve(tok)
        else reject(new Error('Accesso Facebook annullato'))
      },
      { scope: 'email,public_profile' },
    )
  })
}
